import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import isAfter from 'date-fns/isAfter';
import differenceInDays from 'date-fns/differenceInDays';

import { selectShopEventStatus } from '@store/selectors/shopEvent';
import { selectIsUserLogged } from '@selectors/auth';

import { getRouteUrl } from '@utils/urls';

import { SHOP_EVENT_STATUS } from '@common/constants/shopEvent';
import { REGISTER } from '@common/routes';
import TimeCountdown from '@common/components/TimeCountdown';
import { ButtonLink, BUTTON_VARIANTS } from '@common/components/Button';

import {
  Container,
  Title,
  Register,
  RegisterTitle,
  RegisterDescription,
} from './Countdown.styled';

const Countdown = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    shopEventStatus,
    previewOpenFrom,
    shopOpenFrom,
    shopOpenTo,
    flashSaleMode,
  } = useSelector(selectShopEventStatus);
  const isUserLogged = useSelector(selectIsUserLogged);
  const isUpcoming = shopEventStatus === SHOP_EVENT_STATUS.UPCOMING;

  const [maxDays, countTo] = useMemo(() => {
    const shopOpenFromDate = Date.parse(shopOpenFrom);
    const shopOpenToDate = Date.parse(shopOpenTo);
    const previewOpenFromDate = Date.parse(previewOpenFrom);

    return isAfter(Date.now(), shopOpenFromDate)
      ? [differenceInDays(shopOpenToDate, shopOpenFromDate), shopOpenToDate]
      : [
          differenceInDays(shopOpenFromDate, previewOpenFromDate),
          shopOpenFromDate,
        ];
  }, [previewOpenFrom, shopOpenFrom, shopOpenTo]);

  return (
    (isUpcoming || flashSaleMode) && (
      <Container data-testid="countdownWrapper" isUpcoming={isUpcoming}>
        <Title>
          {isUpcoming
            ? t(
                'shopEvent.upcoming.countdownTitle',
                'Chicorée Moments starten in:',
              )
            : t(
                'shopEvent.ongoing.countdownTitle',
                'Chicorée Moments enden in:',
              )}
        </Title>
        <TimeCountdown maxDays={maxDays} time={countTo} />
        {!isUserLogged && isUpcoming && (
          <Register>
            <RegisterTitle>
              {t(
                'shopEvent.upcoming.dontMissEventTitle',
                'Verpasse nicht die Chicorée Moments',
              )}
            </RegisterTitle>
            <RegisterDescription>
              {t(
                'shopEvent.upcoming.dontMissEventDescription',
                'Registriere Dich jetzt als Chicorée VIP und wir informieren Dich rechtzeitig zum Verkaufsstart',
              )}
            </RegisterDescription>
            <Link
              legacyBehavior
              href={REGISTER}
              as={getRouteUrl(language, REGISTER)}
              passHref
            >
              <ButtonLink variant={BUTTON_VARIANTS.LEVEL_1_GREEN_SMALL}>
                {t(
                  'shopEvent.upcoming.dontMissEventRegister',
                  'Zur Registrierung',
                )}
              </ButtonLink>
            </Link>
          </Register>
        )}
      </Container>
    )
  );
};

export default Countdown;
